'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import {
  ADMITTED_EVENT,
  CONNECTION_ERROR_DEFAULT_MESSAGE,
  DISABLED_EVENT,
  QUEUE_STATUS_EVENT,
  WINDOW_UNLOAD_EVENT,
} from '@r-and-a-shared-ui/utils';

type UseSignalRConnectionOptions = {
  userId: string;
  onAdmitted: () => void;
  onDisableRoom: () => void;
  onQueueStatusEvent: (eventData: QueueStatusEventData) => void;
  realtimeEndpoint?: string;
  connectionErrorMessage?: string;
  skip?: boolean;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
};

export type QueueStatusEventData = {
  position?: number;
  progressPercentage?:number
};

export function useSignalRConnection(options: UseSignalRConnectionOptions) {
  const {
    userId,
    onAdmitted,
    onDisableRoom,
    onQueueStatusEvent,
    realtimeEndpoint,
    connectionErrorMessage = CONNECTION_ERROR_DEFAULT_MESSAGE,
    skip = false,
    reconnectDelay = 3000,
    maxReconnectAttempts = 3,
  } = options;

  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>({} as ReturnType<typeof setTimeout>);

  useEffect(() => {
    window.addEventListener(WINDOW_UNLOAD_EVENT, triggerCleanDisconnect, { once: true });
  }, []);

  const triggerCleanDisconnect = async () => {
    if(!connectionRef?.current) {
      return;
    }
    await connectionRef.current.stop();

    connectionRef.current = null;
  };

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    triggerCleanDisconnect();
  }, []);

  const establishConnection = useCallback(async () => {
    if (!realtimeEndpoint || !userId || skip) {
      return;
    }
    try {
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        return;
      }
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${realtimeEndpoint}?userId=${userId}`, {
          withCredentials: true,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext: any) => {
            if (retryContext.previousRetryCount >= maxReconnectAttempts) {
              return null; // Stop retrying after max attempts
            }
            return Math.min(reconnectDelay * Math.pow(2, retryContext.previousRetryCount), 30000);
          }
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      connection.off(ADMITTED_EVENT);
      connection.off(DISABLED_EVENT);
      connection.off(QUEUE_STATUS_EVENT);

      connection.on(ADMITTED_EVENT, onAdmitted);
      connection.on(DISABLED_EVENT, onDisableRoom);
      connection.on(QUEUE_STATUS_EVENT, onQueueStatusEvent);

      connection.onreconnected(() => {
        setError(null);
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      });
      connection.onclose(() => {
        setIsConnected(false);
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            establishConnection();
          }, reconnectDelay);
        } else {
          setError(connectionErrorMessage);
        }
      });

      await connection.start();
      connectionRef.current = connection;
      setIsConnected(true);
      setError(null);
      reconnectAttemptsRef.current = 0;
    } catch (error) {
      console.error('SignalR connection error:', error);
      setError(connectionErrorMessage);
      setIsConnected(false);
    }
  }, [realtimeEndpoint, userId, skip, onAdmitted,
    onDisableRoom, onQueueStatusEvent,
    connectionErrorMessage, reconnectDelay, maxReconnectAttempts]);

  useEffect(() => {
    if (skip) {
      cleanup();
      return;
    }
    establishConnection();

    return cleanup;
  }, [
    skip, cleanup, establishConnection]);

  const reconnect = useCallback(() => {
    cleanup();
    reconnectAttemptsRef.current = 0;
    establishConnection();
  },[cleanup, establishConnection]);

  return { error, isConnected, reconnect };
}
